import React, { useEffect, useState } from 'lib/react';
import {
  EVButton,
  EVClear, EVCircularProgress, EVTypography,
  EVFilterVerticalIcon, EVGrid, EVIconButton, EVSearchIcon, EVSkeleton, makeStyles, EVDownloadIcon,
} from 'lib/components';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import TextInputField from 'commonComponents/textInputField';
import downloadOrderHistoryExcelService from 'services/downloadOrderHistoryExcel.service';
import useMediaQuery from 'hooks/useMediaQuery';
import { hasBlueprintProduct } from 'features/order/Order.utils';
import {
  NOTIFICATION_START, NOTIFICATION_SUCCESS, NOTIFICATION_ERROR, NOTIFICATION_CLOSE,
} from 'constants/ActionTypes';
import useAbortController from 'hooks/useAbortController';
import { CLIENT_ERROR_RESPONSES } from 'constants/AppConstants';
import { loadOrderHistoryPage } from '../OrderHistory.actions';

const useStyles = makeStyles((theme) => ({
  searchRow: {
    padding: '8px',
    minHeight: '112px',
    '&>*': { height: '44px' },
    '& button': {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '19px',
      textTransform: 'none',
    },
    '@media (min-width: 640px)': {
      padding: '24px 24px 16px 24px',
      minHeight: '0px',
      borderBottom: `1px solid ${theme.evPalette.charcoal[200]}`,
    },
  },
  searchContainer: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '@media (min-width: 640px)': {
      flexDirection: 'row-reverse',
      minWidth: ({ loading }) => (loading ? '30%' : 0),
    },
  },
  searchSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    marginLeft: '0px',
    '@media (min-width: 640px)': {
      marginLeft: 'auto',
    },
  },
  downloadExcelBtn: {
    display: 'flex',
    width: '100%',
    border: `1px solid ${theme.evPalette.ocean[600]}`,
    '&>.myev-MuiButton-label': {
      gap: '10px',
      justifyContent: 'center',
    },
  },
  downloadExcelText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    textTransform: 'none',
  },
  searchInput: {
    height: '44px',
    borderColor: theme.evPalette.charcoal[500],
    '@media (min-width: 640px)': { width: '384px' },
  },
  filterButton: ({ showFilters }) => ({
    padding: '10px 12px',
    border: `1px solid ${theme.evPalette.ocean[600]}`,
    borderRadius: '4px',
    ...(showFilters ? {
      backgroundColor: `${theme.evPalette.ocean[800]} !important`,
      color: theme.evPalette.salt[100],
    } : {
      backgroundColor: theme.evPalette.salt[100],
      color: theme.evPalette.ocean[600],
    }),
    '&:hover': {
      ...(showFilters ? {
        backgroundColor: `${theme.evPalette.ocean[800]} !important`,
        color: theme.evPalette.salt[100],
      } : {
        backgroundColor: theme.evPalette.salt[100],
        color: theme.evPalette.ocean[600],
      }),
    },
  }),
}));

function SearchRow(props) {
  const { loading, showFilters, setShowFilters } = props;
  const classes = useStyles({ loading, showFilters });
  const isMobile = useMediaQuery(640);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    userProducts, searchText, reportIds, companyId, orgNodeId,
  } = useSelector(({ orderHistory, userData }) => ({
    userProducts: userData.userProducts,
    searchText: orderHistory.searchText,
    reportIds: orderHistory.reportIds,
    companyId: userData.initParameters?.companyId,
    orgNodeId: userData.initParameters?.orgNodeId,
  }));

  const [searchInput, setSearchInput] = useState(searchText);
  const { getController } = useAbortController();

  useEffect(() => setSearchInput(searchText), [searchText]);

  const downloadExcelRequestBody = {
    reportIds,
    companyId,
    orgNodeId,
    hasBlueprintProduct: hasBlueprintProduct(userProducts),
  };
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const handleDownloadExcel = () => {
    // Obtain a reference to the AbortSignal
    const controller = getController();
    const { signal } = controller;
    const totalReports = reportIds.length;
    setDownloadingExcel(true);
    dispatch({
      type: NOTIFICATION_START,
      title: t('notification_bar__download_excel_progress_title', { totalReports }),
      footer: t('notification_bar__download_excel_progress_footer'),
    });

    downloadOrderHistoryExcelService(downloadExcelRequestBody, signal)
      .then((response) => {
        // #genai nested ternary split into a lazily evaluated helper; same branch order
        const getFailureNotificationType = () => (
          response.status === CLIENT_ERROR_RESPONSES.STATUS_ABORT_REQUEST ? NOTIFICATION_CLOSE : NOTIFICATION_ERROR
        );
        const notificationType = response.ok ? NOTIFICATION_SUCCESS : getFailureNotificationType();
        const title = response.ok ? t('notification_bar__download_excel_success') : t('notification_bar__download_excel_error');

        dispatch({
          type: notificationType,
          title,
          footer: '',
        });
        setDownloadingExcel(false);
      })
      .catch(() => {
        dispatch({
          type: NOTIFICATION_ERROR,
          title: t('notification_bar__download_excel_error'),
          footer: '',
        });
        setDownloadingExcel(false);
      });
  };

  const handleSearchInputChange = ({ target: { value } }) => setSearchInput(value);

  const handleSearch = () => dispatch(loadOrderHistoryPage(searchInput));

  const loadingSkeletons = (
    <>
      <EVSkeleton width={isMobile ? '70%' : '60%'} />
      <EVSkeleton width="30%" />
      {isMobile && <EVSkeleton width="8%" />}
    </>
  );

  return (
    <EVGrid container wrap="nowrap" className={classes.searchRow}>
      <EVGrid container wrap="nowrap" className={classes.searchContainer} component="form">

        {loading ? loadingSkeletons
          : (
            <>
              <EVGrid
                className={classes.searchSection}
              >
                <TextInputField
                  inputId="search-input"
                  inputFieldProps={{
                    InputProps: {
                      startAdornment: <EVSearchIcon htmlColor="#B4BAC7" style={{ marginRight: '14px' }} />,
                      classes: { root: classes.searchInput },
                      ...(searchInput ? {
                        endAdornment: (
                          <EVIconButton id="clear-search-btn" onClick={() => setSearchInput('')} style={{ padding: 0 }}>
                            <EVClear htmlColor="#B4BAC7" />
                          </EVIconButton>
                        ),
                      } : {}),
                    },
                    placeholder: isMobile ? '' : t('order_history__search_placeholder'),
                  }}
                  startAdornment={<EVSearchIcon htmlColor="#B4BAC7" />}
                  inputValue={searchInput}
                  handleChange={handleSearchInputChange}
                />
                <EVButton
                  id="search-btn"
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  type="submit"
                >
                  {t('order_history__search_btn')}
                </EVButton>
              </EVGrid>
              <EVGrid style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                {isMobile && (
                <EVIconButton
                  id="show-filters-btn"
                  className={classes.filterButton}
                  onClick={() => setShowFilters((show) => !show)}
                >
                  <EVFilterVerticalIcon />
                </EVIconButton>
                )}
                {(reportIds?.length > 0) && (
                <EVButton
                  id="download-excel-btn"
                  variant="outlined"
                  color="primary"
                  onClick={handleDownloadExcel}
                  className={classes.downloadExcelBtn}
                >
                  {downloadingExcel ? <EVCircularProgress color="inherit" size={24} />
                    : <EVDownloadIcon id="download-excel-icon" />}
                  <EVTypography className={classes.downloadExcelText}>{t('order_history__download_Excel')}</EVTypography>
                </EVButton>
                )}
              </EVGrid>
            </>
          )}
      </EVGrid>
    </EVGrid>
  );
}
SearchRow.propTypes = {
  loading: PropTypes.bool.isRequired,
  showFilters: PropTypes.bool.isRequired,
  setShowFilters: PropTypes.func.isRequired,
};
export default SearchRow;
